import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, MoreVertical, CheckCircle, DollarSign } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, Table, Pagination, Button, Badge,
    SearchInput, Select, Dropdown, DropdownItem,
    ConfirmDialog, toast
} from '../../components/common';
import { paymentApi } from '../../services/api';
import './Invoices.css';

export default function PaymentList() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [actionPayment, setActionPayment] = useState(null);
    const [actionType, setActionType] = useState('');

    const { data, isLoading } = useQuery({
        queryKey: ['payments', page, pageSize, search, statusFilter],
        queryFn: () => paymentApi.list({ page, limit: pageSize, search, status: statusFilter }),
        select: (res) => res.data
    });

    const actionMutation = useMutation({
        mutationFn: ({ id, action }) => {
            switch (action) {
                case 'approve': return paymentApi.approve(id);
                case 'complete': return paymentApi.complete(id);
                default: throw new Error('Unknown action');
            }
        },
        onSuccess: (_, { action }) => {
            toast.success(`Payment ${action}d successfully`);
            queryClient.invalidateQueries(['payments']);
            setActionPayment(null);
            setActionType('');
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Action failed');
        }
    });

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency', currency: 'IDR', minimumFractionDigits: 0
        }).format(value || 0);
    };

    const getStatusBadge = (status) => {
        const config = {
            'pending': 'warning',
            'approved': 'info',
            'processing': 'warning',
            'completed': 'success',
            'failed': 'danger'
        };
        return <Badge variant={config[status] || 'default'}>{status}</Badge>;
    };

    const columns = [
        {
            key: 'paymentNumber',
            title: 'Payment Number',
            dataIndex: 'paymentNumber',
            render: (num) => <span className="payment-number">{num}</span>
        },
        {
            key: 'invoice',
            title: 'Invoice',
            dataIndex: 'invoice',
            render: (inv) => <span>{inv?.invoiceNumber || '-'}</span>
        },
        {
            key: 'amount',
            title: 'Amount',
            dataIndex: 'amount',
            align: 'right',
            render: (val) => <span className="payment-amount">{formatCurrency(val)}</span>
        },
        {
            key: 'paymentDate',
            title: 'Payment Date',
            dataIndex: 'paymentDate',
            render: (date) => formatDate(date)
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'status',
            render: (status) => getStatusBadge(status)
        },
        {
            key: 'actions',
            title: '',
            width: 50,
            align: 'center',
            render: (_, row) => (
                <Dropdown
                    trigger={<button className="action-btn"><MoreVertical size={16} /></button>}
                    align="right"
                >
                    {({ close }) => (
                        <>
                            <DropdownItem icon={Eye} onClick={() => { close(); navigate(`/payments/${row.id}`); }}>
                                View Details
                            </DropdownItem>
                            {row.status === 'pending' && (
                                <DropdownItem icon={CheckCircle} onClick={() => { close(); setActionPayment(row); setActionType('approve'); }}>
                                    Approve
                                </DropdownItem>
                            )}
                            {row.status === 'approved' && (
                                <DropdownItem icon={DollarSign} onClick={() => { close(); setActionPayment(row); setActionType('complete'); }}>
                                    Complete
                                </DropdownItem>
                            )}
                        </>
                    )}
                </Dropdown>
            )
        }
    ];

    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'processing', label: 'Processing' },
        { value: 'completed', label: 'Completed' }
    ];

    return (
        <div className="payment-page">
            <PageHeader
                title="Payments"
                subtitle="Manage payments"
                breadcrumbs={[{ label: 'Payments', path: '/payments' }]}
                actions={
                    <Button icon={Plus} onClick={() => navigate('/payments/create')}>
                        Create Payment
                    </Button>
                }
            />

            <Card>
                <div className="payment-toolbar">
                    <SearchInput
                        value={search}
                        onChange={setSearch}
                        onSearch={setSearch}
                        placeholder="Search payments..."
                        className="payment-search"
                    />
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        options={statusOptions}
                        className="payment-filter"
                    />
                </div>

                <Table
                    columns={columns}
                    data={data?.data || []}
                    loading={isLoading}
                    emptyText="No payments found"
                    rowKey="id"
                />

                {data?.meta && (
                    <div className="payment-pagination">
                        <Pagination
                            page={page}
                            totalPages={data.meta.totalPages}
                            pageSize={pageSize}
                            total={data.meta.total}
                            onPageChange={setPage}
                            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                        />
                    </div>
                )}
            </Card>

            <ConfirmDialog
                isOpen={!!actionPayment}
                onClose={() => { setActionPayment(null); setActionType(''); }}
                onConfirm={() => actionMutation.mutate({ id: actionPayment?.id, action: actionType })}
                title={`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} Payment`}
                message={`Are you sure you want to ${actionType} this payment?`}
                confirmText={actionType.charAt(0).toUpperCase() + actionType.slice(1)}
                loading={actionMutation.isPending}
            />
        </div>
    );
}
